import React, { useState, useEffect, useCallback } from "react";
import { Percent, Layers } from "lucide-react";
import Taxes from "@/pages/Taxes";
import api from "@/lib/api";
import { Surface, PageSection, formatINR } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const TABS = [
  { key: "tax", label: "Product Tax %", icon: Percent, component: Taxes },
  { key: "plating", label: "Plating Cost", icon: Layers, component: PlatingCostTab },
];

export default function TaxOtherSettings() {
  const [tab, setTab] = useState("tax");
  const active = TABS.find((t) => t.key === tab) || TABS[0];
  const ActiveComp = active.component;
  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="border-b border-gray-200 flex items-center gap-1 px-3">
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = t.key === tab;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} className={`px-4 py-3 text-sm font-semibold flex items-center gap-2 border-b-2 -mb-px transition-colors ${isActive ? "border-[#9B1C1C] text-[#9B1C1C]" : "border-transparent text-gray-500 hover:text-gray-800"}`} data-testid={`tos-tab-${t.key}`}>
                <Icon className="h-4 w-4" />{t.label}
              </button>
            );
          })}
        </div>
      </div>
      <ActiveComp />
    </div>
  );
}

/* -------- Plating Cost Tab (Worker Charge moved to Workers module) -------- */
function PlatingCostTab() { return <RmSettingsTab field="plating_cost" label="Plating Cost / Unit" />; }

function RmSettingsTab({ field, label }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);
  const [catDefaults, setCatDefaults] = useState({});
  const [catDirty, setCatDirty] = useState({});
  const PAGE_SIZE = 100;

  const load = useCallback(async () => {
    try {
      const r = await api.get("/production/rm-settings", { params: { page, page_size: PAGE_SIZE, search: search || undefined } });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setDirty({});
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  }, [page, search]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search]);

  useEffect(() => {
    api.get("/production/rm-category-settings")
      .then((r) => setCatDefaults(Object.fromEntries((r.data || []).map((c) => [c.category, c]))))
      .catch(() => setCatDefaults({}));
  }, []);

  const setPerRow = (id, v) => setDirty((d) => ({ ...d, [id]: v }));
  const dirtyCount = Object.keys(dirty).length;

  const saveAll = async () => {
    if (!dirtyCount) return;
    setSaving(true);
    let ok = 0, fail = 0;
    for (const [id, v] of Object.entries(dirty)) {
      try { await api.patch(`/production/rm-settings/${id}`, { [field]: v === "" ? null : Number(v) }); ok++; }
      catch { fail++; }
    }
    toast[fail ? "error" : "success"](`Saved ${ok}${fail ? ` · ${fail} failed` : ""}`);
    setSaving(false); load();
  };

  const saveCategoryDefault = async (cat) => {
    const v = catDirty[cat];
    if (v === undefined) return;
    try {
      await api.put(`/production/rm-category-settings/${encodeURIComponent(cat)}`, { [field]: v === "" ? null : Number(v) });
      toast.success(`Category "${cat}" default saved`);
      const r = await api.get("/production/rm-category-settings");
      setCatDefaults(Object.fromEntries((r.data || []).map((c) => [c.category, c])));
      setCatDirty((s) => { const c = { ...s }; delete c[cat]; return c; });
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const categoriesOnPage = Array.from(new Set(items.map((it) => it.category_id || it.category).filter(Boolean)));

  return (
    <PageSection title={label} description={`Per-RM value plus a category-level default fallback. Applied automatically when receiving a fabrication batch.`}
      action={<Button className="bg-[#9B1C1C] hover:bg-[#7F1D1D] gap-1.5" onClick={saveAll} disabled={saving || !dirtyCount} data-testid="rms-save">
        {saving ? "Saving…" : dirtyCount ? `Save ${dirtyCount} change${dirtyCount === 1 ? "" : "s"}` : "No changes"}
      </Button>}
    >
      {categoriesOnPage.length > 0 && (
        <Surface padded={false} className="mb-3">
          <div className="p-3 border-b border-gray-200 text-xs font-semibold text-gray-700">Category-Level Defaults (fallback when per-RM is blank)</div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-3 py-2">Category</th>
                <th className="px-3 py-2 text-right w-40">{label}</th>
                <th className="px-3 py-2 w-24"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {categoriesOnPage.map((cat) => {
                const cur = catDefaults[cat] || {};
                const val = catDirty[cat] !== undefined ? catDirty[cat] : (cur[field] ?? "");
                return (
                  <tr key={cat}>
                    <td className="px-3 py-2 font-medium">{cat}</td>
                    <td className="px-3 py-2"><Input type="number" step="0.01" value={val} onChange={(e) => setCatDirty((s) => ({ ...s, [cat]: e.target.value }))} className="text-right tabular-nums" data-testid={`rms-cat-${cat}`} /></td>
                    <td className="px-3 py-2"><Button size="sm" variant="outline" disabled={catDirty[cat] === undefined} onClick={() => saveCategoryDefault(cat)}>Save</Button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Surface>
      )}

      <Surface padded={false}>
        <div className="p-3 border-b border-gray-200 flex items-center gap-2">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search RM name / code…" className="max-w-sm" data-testid="rms-search" />
          <span className="ml-auto text-xs text-gray-500">{total.toLocaleString()} RM(s)</span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
              <th className="px-3 py-2">Raw Material</th>
              <th className="px-3 py-2">Category</th>
              <th className="px-3 py-2 text-center w-16">Unit</th>
              <th className="px-3 py-2 text-right w-32">Category Default</th>
              <th className="px-3 py-2 text-right w-40">{label}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-gray-400 text-xs">No RMs found.</td></tr>}
            {items.map((rm) => {
              const cur = rm[field];
              const val = dirty[rm.id] !== undefined ? dirty[rm.id] : (cur ?? "");
              const catDef = rm.category_default?.[field];
              return (
                <tr key={rm.id} className={dirty[rm.id] !== undefined ? "bg-amber-50/40" : ""}>
                  <td className="px-3 py-2">
                    <div className="font-medium">{rm.name}</div>
                    <div className="text-[11px] font-mono text-gray-500">{rm.code || "—"}</div>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600">{rm.category || "—"}</td>
                  <td className="px-3 py-2 text-center text-xs text-gray-600">{rm.unit || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs text-gray-500">{catDef != null ? formatINR(catDef) : "—"}</td>
                  <td className="px-3 py-2"><Input type="number" step="0.01" value={val} onChange={(e) => setPerRow(rm.id, e.target.value)} placeholder={catDef != null ? `(default ${catDef})` : "blank = 0"} className="text-right tabular-nums" data-testid={`rms-row-${rm.id}`} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Surface>
      {total > PAGE_SIZE && (
        <div className="mt-2 flex items-center justify-end gap-2 text-xs">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</Button>
          <span>Page {page} of {Math.ceil(total / PAGE_SIZE)}</span>
          <Button size="sm" variant="outline" disabled={page * PAGE_SIZE >= total} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      )}
    </PageSection>
  );
}
